import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { makeGet, makePost, makeDelete } from "@culturehq/client";
import { Loader, PlainButton, Success, Warning } from "@culturehq/components";

import { useProfile } from "app/context";
import { parseQueryString, t, Trans } from "app/utils";
import apiBase from "constants/apiBase";

import ErrorMessage from "components/common/ErrorMessage";
import PageHeader from "components/display/PageHeader";

import icon from "images/add_to_slack.png";
import icon2x from "images/add_to_slack@2x.png";
import ChqModalConfirmDelete from "components/common/ChqModalConfirmDelete";

const SlackIntegrationConfigure = ({ error }) => (
  <>
    {
      error
        ? <ErrorMessage error={error} />
        : <Warning><Trans>No integration is currently configured.</Trans></Warning>
    }
    <p>
      <Trans>
        To configure a new integration, click the following link and follow the
        prompts from Slack to authenticate and grant CultureHQ the necessary
        permissions.
      </Trans>
      {" "}
      <a href={`${apiBase}/slack/install`}>
        <img
          alt={t("Add to Slack")}
          height="40"
          width="139"
          src={icon}
          srcSet={`${icon} 1x, ${icon2x} 2x`}
        />
      </a>
    </p>
  </>
);

const SlackIntegrationSuccess = ({ slackIntegration, onDelete }) => (
  <>
    <Success>
      <Trans>
        The integration has been configured successfully. When broadcasting,
        it will post updates to {slackIntegration.channel}.
      </Trans>
    </Success>
    <p>
      <ChqModalConfirmDelete
        onAccept={onDelete}
        trigger={onTrigger => (
          <Trans>
            You may click here to{" "}
            <PlainButton onClick={onTrigger}>delete your integration</PlainButton>.
          </Trans>
        )}
      >
        <Trans>Are you sure you would like to delete the slack integration?</Trans>
      </ChqModalConfirmDelete>
    </p>
  </>
);

const getLearnMoreUrl = profile => {
  if (profile.organizationAdmin) {
    return "https://intercom.help/culturehq/for-company-administrators/culturehq-and-slack-integration";
  }
  return "https://intercom.help/culturehq/for-all-other-users/slack-integration/culturehq-and-slack-integration";
};

const SlackIntegration = () => {
  const { profile } = useProfile();
  const location = useLocation();

  const [fetching, setFetching] = useState(true);
  const [slackIntegration, setSlackIntegration] = useState(null);
  const [error, setError] = useState(null);

  useEffect(
    () => {
      let cancelled = false;
      const { code } = parseQueryString(location.search);

      let promise;

      if (code) {
        promise = makePost("/slack_integration", { code });
      } else {
        promise = makeGet("/slack_integration");
      }

      promise
        .then(response => {
          if (!cancelled) {
            setSlackIntegration(response.slackIntegration);
            setFetching(false);
          }
        })
        .catch(caught => {
          if (!cancelled) {
            if (caught.status !== 404) {
              setError(caught);
            }
            setFetching(false);
          }
        });

      return () => {
        cancelled = true;
      };
    },
    [location.search, setError, setSlackIntegration]
  );

  const handleDelete = async () => {
    try {
      await makeDelete("/slack_integration");
      setSlackIntegration(null);
      setError(null);
    } catch (caught) {
      setError(caught);
    }
  };

  return (
    <>
      <PageHeader title={t("Slack Integrations")}>
        <Trans>
          Here you can configure an integration with Slack. The integration will
          broadcast events from within CultureHQ and allow interacting with
          CultureHQ through Slack&#39;s slash commands. You can click here
          to <a href={getLearnMoreUrl(profile)}>learn more</a>.
        </Trans>
      </PageHeader>
      <Loader loading={fetching}>
        {
          slackIntegration
            ? (
              <SlackIntegrationSuccess
                slackIntegration={slackIntegration}
                onDelete={handleDelete}
              />
            )
            : <SlackIntegrationConfigure error={error} />
        }
      </Loader>
    </>
  );
};

export default SlackIntegration;
